import generalSettings from "@apis/generalSettings";
import { deletesvg, edit, switchOff, switchOn } from "@assets/icons";
import CardHeader from "@commons/businesscardHeader";
import Button from "@components/button";
import DeleteModal from "@components/delete-modal";
import Fields from "@components/fields";
import Row from "@components/row";
import Svg from "@components/svg";
import Table from "@components/table";
import Wrapper from "@components/wrapper";
import { useModal } from "@context/ModalContext";
import { RootState } from "@store/index";
import { Form, Formik } from "formik";
import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

interface CancellationReason {
  id: string;
  key: string;
  value: string;
  infos: {
    status: boolean;
  };
}

interface FormValues {
  reason: string;
  userType: string;
  id?: string;
}

const Cancellation = () => {
  const { showModal, hideModal } = useModal();
  const [initialValues, setInitialValues] = useState<FormValues>({
    reason: "",
    userType: "",
    id: undefined,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const { data: cancellations } = useSelector(
    (state: RootState) => state.cancellations
  );

  const fetchCancellations = useCallback(async () => {
    await generalSettings
      .getByGroup({
        state: "cancellations",
        id: "cancellations",
      })
      .then(() => {})
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchCancellations();
  }, [fetchCancellations]);

  const resetForm = () => {
    setInitialValues({
      reason: "",
      userType: "",
      id: undefined,
    });
    setIsEditing(false);
  };

  const handleSubmit = async (
    values: FormValues,
    { resetForm: formikReset }: any
  ) => {
    if (!values.reason.trim() || !values.userType) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);

    const payload = {
      group: "cancellations",
      key: `${values.userType} - ${Math.random()}`,
      value: values.reason.trim(),
      title: values.userType,
      infos: {
        status: true,
      },
    };

    try {
      if (isEditing && values.id) {
        await generalSettings.update({
          state: "cancellations",
          data: payload,
          id: values.id,
        });
        toast.success("Cancellation reason updated successfully");
      } else {
        // Create new cancellation reason
        await generalSettings.create({
          state: "cancellations",
          data: payload,
        });
        toast.success("Cancellation reason created successfully");
      }

      resetForm();
      formikReset();
      fetchCancellations();
    } catch (error) {
      console.error(
        `Failed to ${isEditing ? "update" : "create"} cancellation reason:`,
        error
      );
      toast.error(
        `Failed to ${isEditing ? "update" : "create"} cancellation reason`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStatusToggle = async (item: CancellationReason) => {
    const payload = {
      key: item.key,
      infos: {
        status: !item.infos.status,
      },
    };

    try {
      await generalSettings.update({
        state: "cancellations",
        data: payload,
        id: item.id,
      });
      toast.success(
        `Status ${item.infos.status ? "disabled" : "enabled"} successfully`
      );
      fetchCancellations();
    } catch (error) {
      console.error("Failed to update status:", error);
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (item: CancellationReason) => {
    showModal(
      <DeleteModal
        state="cancellations"
        api={generalSettings}
        id={item.id}
        onSuccess={() => fetchCancellations()}
      />
    );
  };

  const handleEdit = (item: CancellationReason) => {
    setInitialValues({
      reason: item.value,
      userType: item.key,
      id: item.id,
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    resetForm();
  };

  const userTypeOptions = [
    { title: "Admin", key: "admin" },
    { title: "Store", key: "store" },
    { title: "Customer", key: "customer" },
    { title: "Deliveryman", key: "deliveryman" },
  ];

  const columns = [
    {
      title: "SN",
      key: "sn",
      render: (item: CancellationReason, index: number) => index + 1,
    },
    {
      title: "Reason",
      key: "reason",
      render: (item: CancellationReason) => item.value,
    },
    {
      title: "Type",
      key: "type",
      render: (item: any) => item.title,
    },
    {
      title: "Status",
      key: "status",
      render: (item: CancellationReason) => (
        <Svg
          src={item.infos.status ? switchOn : switchOff}
          onClick={() => handleStatusToggle(item)}
          title={item.infos.status ? "Disable" : "Enable"}
        />
      ),
    },
  ];

  const tableActions = [
    {
      key: "remove",
      icon: deletesvg,
      onClick: handleDelete,
      title: "Delete reason",
    },
    {
      key: "edit",
      icon: edit,
      onClick: handleEdit,
      title: "Edit reason",
    },
  ];

  return (
    <>
      <CardHeader title="Cancellation Settings" />
      <Wrapper>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ values }) => (
            <Form>
              <Row>
                <Fields.Input
                  label="Order Cancellation Reason"
                  name="reason"
                  placeholder="Ex: Item is Broken"
                  required
                />
                <Fields.DropDown
                  name="userType"
                  label="User Type"
                  options={userTypeOptions}
                  titleAsKey="title"
                  valueAsKey="key"
                  styleInput="width:97.5%"
                  placeholder="---select user type---"
                />
              </Row>
              <br />
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                }}
              >
                {isEditing && (
                  <Button
                    type="button"
                    title="Cancel"
                    onClick={handleCancelEdit}
                    variant="secondary"
                  />
                )}
                <Button
                  justify="end"
                  title={
                    isSubmitting
                      ? isEditing
                        ? "Updating..."
                        : "Creating..."
                      : isEditing
                        ? "Update"
                        : "Create"
                  }
                  type="submit"
                  disabled={
                    isSubmitting || !values.reason.trim() || !values.userType
                  }
                />
              </div>
            </Form>
          )}
        </Formik>
      </Wrapper>

      <br />

      <CardHeader title="Order cancellation reason list" />
      <Wrapper>
        <Table
          hasSearchbar={false}
          data={cancellations || []}
          columns={columns}
          actions={tableActions}
        />
      </Wrapper>
    </>
  );
};

export default Cancellation;
